import { formatQuantity } from '@api/BranchOfficeInventories/BranchOfficeInventory';

// ============================================================================
// EL ESTADO DE UN INSUMO EN UNA SUCURSAL, EN DÍAS (plan de inventario F4).
//
// Hasta F4 el estado comparaba presentaciones contra tres umbrales del catálogo,
// iguales para las 10 sucursales y el CEDIS: con el bug de F0 arreglado, 803 de
// 837 renglones seguían en "Crítico" (59 insumos traen el 100/50/10 por defecto;
// ninguna sucursal tiene 100 bolsas de nada). Lo que sí describe a una sucursal es
// CUÁNTOS DÍAS le dura lo que tiene al ritmo al que ELLA lo gasta: Av. Juárez
// gasta 11.8 kg de papas al día y Zavaleta 1.8 kg; el mismo saldo son 1.5 días en
// una y 9 en la otra.
//
// El ritmo sale de `inventory-usage/{sucursal}` (lo escribe el aseo, F3/F4) y los
// umbrales en días se ajustan POR SUCURSAL (`branch_offices.stockDays`, lo edita
// el admin de sucursal; decisión del negocio, 13-oct-2026), con un default en
// código. Lo que no sale por receta (limpieza, empaques, uniformes) no tiene ritmo
// y queda sin estado ("—"): el negocio no lo contabiliza.
// ============================================================================

export interface StockDays {
    /** Menos de estos días de inventario = Crítico. */
    critical: number;
    /** Menos de estos días = Bajo. */
    low: number;
}

/** Propuesta del plan (§8.2): el CEDIS surte cada 3–4 días. Cada sucursal lo ajusta. */
export const STOCK_DAYS_DEFAULT: StockDays = { critical: 2, low: 5 };

/** Desde cuántos días con ventas el ritmo sirve para calcular días (paridad con el server). */
export const MIN_DAYS_WITH_SALES = 3;

/** Los umbrales de la sucursal, o el default; `low` nunca por debajo de `critical`. */
export const getStockDays = (branch?: { stockDays?: Partial<StockDays> }): StockDays => {
    const critical = Number(branch?.stockDays?.critical);
    const low = Number(branch?.stockDays?.low);
    const resolvedCritical = critical > 0 ? critical : STOCK_DAYS_DEFAULT.critical;
    const resolvedLow = low > 0 ? low : STOCK_DAYS_DEFAULT.low;
    return { critical: resolvedCritical, low: Math.max(resolvedLow, resolvedCritical) };
};

export type StockStatusColor = 'error' | 'warning' | 'success' | 'default';

export interface StockStatusByDays {
    label: 'Crítico' | 'Stock bajo' | 'Suficiente' | '—';
    color: StockStatusColor;
    /** Días de inventario, un decimal; `null` sin ritmo. */
    days: number | null;
    /** El porqué, para el tooltip: "≈ 1.5 días · gasta 11,814 gr/día". */
    reason: string;
}

/** Lo que dura lo disponible al ritmo diario; `null` sin ritmo. Un saldo negativo son 0 días. */
export const getDaysOfStock = (available: number, perDay?: number): number | null => {
    const rate = Number(perDay);
    if (!Number.isFinite(rate) || rate <= 0) return null;
    return Math.round((Math.max(Number(available) || 0, 0) / rate) * 10) / 10;
};

/**
 * El estado de un renglón. `perDay` = unidades base por día de ESTA sucursal (de
 * `inventory-usage`); `undefined` = sin ritmo (no sale por receta, o la sucursal aún
 * no junta `MIN_DAYS_WITH_SALES` días con datos) → "—", nunca "Crítico" por falta de dato.
 */
export const getStockStatusByDays = (
    available: number,
    perDay: number | undefined,
    unit: string,
    thresholds: StockDays = STOCK_DAYS_DEFAULT
): StockStatusByDays => {
    const days = getDaysOfStock(available, perDay);
    if (days === null) {
        return { label: '—', color: 'default', days: null, reason: 'Sin consumo por receta en los últimos 14 días' };
    }
    const rate = `gasta ${formatQuantity(Number(perDay))} ${unit}/día`.trim();
    if ((Number(available) || 0) <= 0) {
        return { label: 'Crítico', color: 'error', days: 0, reason: `Sin existencia · ${rate}` };
    }
    const reason = `≈ ${formatQuantity(days, 1)} días · ${rate}`;
    if (days < thresholds.critical) return { label: 'Crítico', color: 'error', days, reason };
    if (days < thresholds.low) return { label: 'Stock bajo', color: 'warning', days, reason };
    return { label: 'Suficiente', color: 'success', days, reason };
};

/** "La sucursal lleva 2 días con datos; desde 3 se calculan los días" — cuando el ritmo aún no sirve. */
export const describeUsageWarmup = (daysWithSales: number | undefined): string =>
    daysWithSales === undefined
        ? 'Todavía no hay ritmo de consumo para esta sucursal (se calcula de madrugada).'
        : `La sucursal lleva ${daysWithSales} día(s) con datos; desde ${MIN_DAYS_WITH_SALES} se calculan los días de inventario.`;
